import { z } from "zod";

import { albumTypeEnum } from "./albumTypes";
import { catalogIdSchema } from "./catalogId";
import { FEATURE_PAGE_FAMILIES } from "./featurePagePaths";
import { formatCategoryEnum } from "./formatGroups";
import { isPlainObject } from "./isPlainObject";
import { localeSchema } from "./localeSchema";

export const featurePageFamilySchema = z
  .enum(FEATURE_PAGE_FAMILIES)
  .describe("Editorial page family: genre, style, collection, or story.");

export const FEATURE_PAGE_ENTITY_TYPES = ["artist", "work", "release", "company"] as const;
export const featurePageEntityTypeSchema = z
  .enum(FEATURE_PAGE_ENTITY_TYPES)
  .describe("Catalog entity type: artist, work, release, or company.");

export const FEATURE_PAGE_ENTITY_RELATION_ROLES = [
  "primarySubject",
  "featured",
  "related",
] as const;
export const featurePageEntityRelationRoleSchema = z
  .enum(FEATURE_PAGE_ENTITY_RELATION_ROLES)
  .describe("Editorial relationship role assigned to a referenced catalog entity.");

export const FEATURE_PAGE_BLOCK_CATALOG = [
  { type: "hero", variants: ["cinematic", "split", "centered"] },
  { type: "richText", variants: [] },
  { type: "entitySpotlight", variants: ["left", "right"] },
  { type: "entityCollection", variants: ["grid", "rail"] },
  { type: "factGrid", variants: ["two", "four"] },
  { type: "timeline", variants: [] },
  { type: "gallery", variants: [] },
  { type: "pullQuote", variants: [] },
  { type: "relatedPages", variants: [] },
  { type: "searchCta", variants: [] },
  { type: "sources", variants: [] },
] as const;

export const featurePageVisualSourceSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("catalogEntity").describe("Use an image resolved from a catalog entity."),
    catalogRefId: z.uuid().describe("Catalog reference UUID supplying the visual."),
    imageRole: z
      .enum(["portrait", "cover", "background"])
      .describe("Catalog image role to resolve."),
  }),
  z.object({
    kind: z.literal("asset").describe("Use an uploaded bundle asset."),
    assetId: z.uuid().describe("Asset UUID supplying the visual."),
  }),
]);

export const featurePageBackgroundSchema = z.object({
  source: featurePageVisualSourceSchema.describe("Visual source used as the block background."),
  treatment: z
    .enum(["cover", "blurredCover", "contained"])
    .default("cover")
    .describe("Background image sizing and blur treatment."),
  overlay: z
    .enum(["light", "medium", "strong"])
    .default("medium")
    .describe("Contrast overlay strength applied above the background."),
  focalPoint: z
    .object({
      x: z.number().min(0).max(1).describe("Horizontal focal position from 0 to 1."),
      y: z.number().min(0).max(1).describe("Vertical focal position from 0 to 1."),
    })
    .optional()
    .describe("Optional normalized focal point used when cropping the background."),
});

const featurePageBlockBaseSchema = z.object({
  id: z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]*$/)
    .describe("Stable kebab-case block identifier unique within the document."),
  heading: z
    .string()
    .min(1)
    .max(160)
    .optional()
    .describe("Optional reader-facing section heading."),
  width: z
    .enum(["prose", "content", "wide", "fullBleed"])
    .default("content")
    .describe("Maximum layout width allocated to the block."),
  spacing: z
    .enum(["compact", "normal", "immersive"])
    .default("normal")
    .describe("Vertical spacing rhythm around the block."),
  surface: z
    .enum(["plain", "tinted", "card", "image"])
    .default("plain")
    .describe("Visual surface treatment applied to the block."),
  background: featurePageBackgroundSchema
    .optional()
    .describe("Optional visual background configuration for the block."),
});

export const featurePageEntitySelectorSchema = z
  .object({
    genres: z
      .array(z.string().min(1))
      .max(8)
      .optional()
      .describe("Genre names used to select catalog entities."),
    styles: z
      .array(z.string().min(1))
      .max(12)
      .optional()
      .describe("Style names used to select catalog entities."),
    artistCatalogRefId: z
      .uuid()
      .optional()
      .describe("Optional artist catalog reference used to scope the selection."),
    sort: z
      .enum(["catalogPopularity", "oldest", "newest", "rating"])
      .default("catalogPopularity")
      .describe("Catalog ordering applied to dynamically selected entities."),
    limit: z
      .number()
      .int()
      .min(1)
      .max(50)
      .default(12)
      .describe("Maximum number of dynamically selected entities."),
  })
  .refine(
    (value) => Boolean(value.genres?.length || value.styles?.length || value.artistCatalogRefId),
    "A selector needs at least one genre, style, or artist reference",
  );

export const featurePageCollectionAnnotationSchema = z.object({
  badge: z
    .string()
    .min(1)
    .max(40)
    .optional()
    .describe("Optional compact badge for the referenced item."),
  note: z
    .string()
    .min(1)
    .max(240)
    .optional()
    .describe("Optional editorial annotation for the referenced item."),
});

const featurePageCollectionAnnotationRecordSchema = z.record(
  z.uuid(),
  featurePageCollectionAnnotationSchema,
);

function addParsedIssue(
  context: z.core.$RefinementCtx,
  issue: z.core.$ZodIssue & Record<string, unknown>,
) {
  context.addIssue(issue);
}

const featurePageCollectionAnnotationsSchema = z
  .unknown()
  .superRefine((value, context) => {
    const result = featurePageCollectionAnnotationRecordSchema.safeParse(value);
    if (result.success) return;

    for (const issue of result.error.issues) {
      if (isPlainObject(issue)) addParsedIssue(context, issue);
    }
  })
  .describe(
    "Per-reference annotations object keyed by catalog reference UUID. Each value may contain badge (1-40 characters) and note (1-240 characters).",
  ) as z.ZodType<z.infer<typeof featurePageCollectionAnnotationRecordSchema>>;

const featurePageHeroBlockSchema = featurePageBlockBaseSchema.extend({
  type: z.literal("hero").describe("Hero block discriminator."),
  variant: z
    .enum(["cinematic", "split", "centered"])
    .default("cinematic")
    .describe("Hero layout variant."),
  eyebrow: z.string().max(100).optional().describe("Optional short label above the hero title."),
  title: z.string().min(1).max(180).describe("Primary reader-facing hero title."),
  body: z.string().min(1).max(600).describe("Introductory hero copy."),
  chips: z
    .array(z.string().min(1).max(50))
    .max(8)
    .optional()
    .describe("Optional compact labels displayed in the hero."),
  visual: featurePageVisualSourceSchema.optional().describe("Optional primary hero visual."),
  cta: z
    .object({
      label: z.string().min(1).max(80).describe("Reader-facing call-to-action label."),
      href: z.string().min(1).max(500).describe("Call-to-action destination path or URL."),
    })
    .optional()
    .describe("Optional hero call to action."),
});

const featurePageRichTextBlockSchema = featurePageBlockBaseSchema.extend({
  type: z.literal("richText").describe("Rich-text block discriminator."),
  paragraphs: z
    .array(
      z.object({
        heading: z
          .string()
          .min(1)
          .max(160)
          .optional()
          .describe("Optional heading for this paragraph group."),
        text: z.string().min(1).max(4_000).describe("Reader-facing prose paragraph."),
        sourceIds: z
          .array(z.string().min(1))
          .max(12)
          .optional()
          .describe("Source IDs supporting claims in this paragraph."),
      }),
    )
    .min(1)
    .max(30)
    .describe("Ordered prose paragraphs in the rich-text block."),
});

const featurePageSpotlightBlockSchema = featurePageBlockBaseSchema.extend({
  type: z.literal("entitySpotlight").describe("Entity-spotlight block discriminator."),
  catalogRefId: z.uuid().describe("Catalog reference UUID featured by this spotlight."),
  imageRole: z
    .enum(["portrait", "cover", "background"])
    .default("cover")
    .describe("Catalog image role used for the spotlight visual."),
  imageSide: z
    .enum(["left", "right"])
    .default("left")
    .describe("Side on which the spotlight visual is displayed."),
  title: z.string().min(1).max(180).optional().describe("Optional spotlight title override."),
  body: z.string().min(1).max(2_000).describe("Editorial explanation of the featured entity."),
  facts: z
    .array(
      z.object({
        label: z.string().min(1).max(80).describe("Short fact label."),
        value: z.string().min(1).max(240).describe("Reader-facing fact value."),
        sourceIds: z
          .array(z.string().min(1))
          .max(8)
          .optional()
          .describe("Source IDs supporting this fact."),
      }),
    )
    .max(8)
    .optional()
    .describe("Optional structured facts about the featured entity."),
});

const featurePageCollectionBlockSchema = featurePageBlockBaseSchema
  .extend({
    type: z.literal("entityCollection").describe("Entity-collection block discriminator."),
    entityType: featurePageEntityTypeSchema.describe(
      "Catalog entity type displayed by this collection block.",
    ),
    variant: z.enum(["grid", "rail"]).default("grid").describe("Collection presentation layout."),
    mode: z
      .enum(["curated", "query", "hybrid"])
      .describe("Whether items are pinned, selected dynamically, or combined."),
    pinnedCatalogRefIds: z
      .array(z.uuid())
      .max(50)
      .optional()
      .describe("Ordered catalog reference UUIDs pinned into the collection."),
    selector: featurePageEntitySelectorSchema
      .optional()
      .describe("Dynamic catalog selector used by query and hybrid modes."),
    annotations: featurePageCollectionAnnotationsSchema.optional(),
  })
  .superRefine((value, context) => {
    if (value.mode !== "query" && !value.pinnedCatalogRefIds?.length) {
      context.addIssue({ code: "custom", message: `${value.mode} collections need pinned refs` });
    }
    if (value.mode !== "curated" && !value.selector) {
      context.addIssue({ code: "custom", message: `${value.mode} collections need a selector` });
    }
  });

const featurePageFactGridBlockSchema = featurePageBlockBaseSchema.extend({
  type: z.literal("factGrid").describe("Fact-grid block discriminator."),
  columns: z
    .enum(["two", "four"])
    .default("four")
    .describe("Number of fact columns at wide layouts."),
  facts: z
    .array(
      z.object({
        label: z.string().min(1).max(80).describe("Short fact label."),
        value: z.string().min(1).max(300).describe("Primary reader-facing fact value."),
        detail: z.string().max(500).optional().describe("Optional supporting fact detail."),
        sourceIds: z
          .array(z.string().min(1))
          .max(8)
          .optional()
          .describe("Source IDs supporting this fact."),
      }),
    )
    .min(1)
    .max(16)
    .describe("Facts displayed by the grid."),
});

const featurePageTimelineBlockSchema = featurePageBlockBaseSchema.extend({
  type: z.literal("timeline").describe("Timeline block discriminator."),
  items: z
    .array(
      z.object({
        label: z.string().min(1).max(80).describe("Timeline date, era, or sequence label."),
        title: z.string().min(1).max(180).describe("Timeline event title."),
        body: z.string().min(1).max(1_000).describe("Editorial timeline event explanation."),
        visual: featurePageVisualSourceSchema
          .optional()
          .describe("Optional visual associated with the event."),
        sourceIds: z
          .array(z.string().min(1))
          .max(8)
          .optional()
          .describe("Source IDs supporting this event."),
      }),
    )
    .min(2)
    .max(30)
    .describe("Chronological or thematic timeline events."),
});

const featurePageGalleryBlockSchema = featurePageBlockBaseSchema.extend({
  type: z.literal("gallery").describe("Gallery block discriminator."),
  items: z
    .array(
      z.object({
        visual: featurePageVisualSourceSchema.describe("Visual displayed by this gallery item."),
        caption: z.string().max(400).optional().describe("Optional reader-facing image caption."),
        credit: z.string().max(240).optional().describe("Optional visible image credit."),
      }),
    )
    .min(2)
    .max(24)
    .describe("Ordered gallery visuals and their captions."),
});

const featurePagePullQuoteBlockSchema = featurePageBlockBaseSchema.extend({
  type: z.literal("pullQuote").describe("Pull-quote block discriminator."),
  quote: z.string().min(1).max(1_000).describe("Quoted text displayed prominently."),
  attribution: z.string().min(1).max(180).describe("Person or publication attributed."),
  context: z.string().max(400).optional().describe("Optional context for the quotation."),
  sourceId: z.string().min(1).optional().describe("Optional source ID documenting the quotation."),
});

const featurePageRelatedPagesBlockSchema = featurePageBlockBaseSchema.extend({
  type: z.literal("relatedPages").describe("Related-pages block discriminator."),
  pages: z
    .array(
      z.object({
        family: featurePageFamilySchema.describe("Family of the related editorial page."),
        slug: z
          .string()
          .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
          .describe("Canonical slug of the related editorial page."),
        label: z.string().min(1).max(100).describe("Reader-facing related-page label."),
        description: z
          .string()
          .max(240)
          .optional()
          .describe("Optional short explanation of the related page."),
      }),
    )
    .min(1)
    .max(12)
    .describe("Editorial pages recommended as related reading."),
});

export const featurePageSearchCtaQuerySchema = z.object({
  artistIds: z
    .array(catalogIdSchema)
    .min(1)
    .max(20)
    .optional()
    .describe(
      "Optional catalog artist IDs serialized through repeated offers-page artistId filters.",
    ),
  q: z.string().min(1).max(200).optional().describe("Optional catalog search query."),
  genre: z.string().min(1).optional().describe("Optional genre filter."),
  style: z.string().min(1).optional().describe("Optional style filter."),
  albumType: albumTypeEnum
    .optional()
    .describe("Optional album type serialized through the catalog search type filter."),
  format: formatCategoryEnum.optional().describe("Optional physical format filter."),
  country: z.string().min(1).optional().describe("Optional release country filter."),
  origin: z
    .string()
    .min(1)
    .optional()
    .describe("Optional primary artist country code passed through the search origin filter."),
  year: z
    .array(z.number().int().min(0).max(9_999))
    .min(1)
    .max(2)
    .optional()
    .describe("Optional release year or inclusive year range."),
  labels: z
    .array(z.string().min(1))
    .min(1)
    .max(20)
    .optional()
    .describe("Optional record labels serialized through the catalog search label filter."),
  company: z
    .array(z.string().min(1))
    .min(1)
    .max(20)
    .optional()
    .describe("Optional company filters."),
});

export type FeaturePageSearchCtaQuery = z.infer<typeof featurePageSearchCtaQuerySchema>;

const featurePageSearchCtaBlockSchema = featurePageBlockBaseSchema.extend({
  type: z.literal("searchCta").describe("Contextual catalog call-to-action block discriminator."),
  title: z.string().min(1).max(160).describe("Call-to-action title."),
  body: z.string().min(1).max(500).describe("Call-to-action explanatory copy."),
  label: z.string().min(1).max(80).describe("Reader-facing action label."),
  ...featurePageSearchCtaQuerySchema.shape,
});

const featurePageSourcesBlockSchema = featurePageBlockBaseSchema.extend({
  type: z.literal("sources").describe("Sources-list block discriminator."),
});

export const featurePageBlockSchema = z.discriminatedUnion("type", [
  featurePageHeroBlockSchema,
  featurePageRichTextBlockSchema,
  featurePageSpotlightBlockSchema,
  featurePageCollectionBlockSchema,
  featurePageFactGridBlockSchema,
  featurePageTimelineBlockSchema,
  featurePageGalleryBlockSchema,
  featurePagePullQuoteBlockSchema,
  featurePageRelatedPagesBlockSchema,
  featurePageSearchCtaBlockSchema,
  featurePageSourcesBlockSchema,
]);

export const featurePageDocumentSchema = z.object({
  blocks: z
    .array(featurePageBlockSchema)
    .min(0)
    .max(40)
    .describe("Ordered editorial blocks rendered in the page document."),
});

export const featurePageSeoSchema = z.object({
  title: z.string().max(180).describe("Search-engine title for the locale revision."),
  description: z.string().max(320).describe("Search-engine description for the locale revision."),
  socialTitle: z
    .string()
    .min(1)
    .max(180)
    .optional()
    .describe("Optional social-sharing title override."),
  socialDescription: z
    .string()
    .min(1)
    .max(320)
    .optional()
    .describe("Optional social-sharing description override."),
  image: featurePageVisualSourceSchema
    .optional()
    .describe("Optional social and search preview image."),
});

export const featurePageThemeSchema = z.object({
  accent: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i)
    .optional()
    .describe("Optional six-digit hexadecimal accent color."),
  surfaceStyle: z
    .enum(["light", "dark", "warm", "cool"])
    .default("dark")
    .describe("Base color and surface mood for the page."),
});

export const featurePageCatalogRefSchema = z.object({
  id: z.uuid().describe("Bundle-local UUID used by blocks and relationships."),
  entityType: featurePageEntityTypeSchema.describe("Type of referenced catalog entity."),
  catalogId: catalogIdSchema.describe("Stable Garimpa catalog ID of the referenced entity."),
});

export const featurePageEntityRelationSchema = z.object({
  catalogRefId: z.uuid().describe("Catalog reference UUID participating in the page."),
  role: featurePageEntityRelationRoleSchema
    .default("related")
    .describe("Editorial role assigned to the entity."),
  sortOrder: z
    .number()
    .int()
    .min(0)
    .max(10_000)
    .default(0)
    .describe("Stable ascending order among entities with the same role."),
});

export const featurePageSourceSchema = z.object({
  id: z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]*$/)
    .describe("Bundle-local source ID cited by editorial blocks."),
  url: z.url().describe("Canonical source URL."),
  title: z.string().min(1).max(500).describe("Source title."),
  publisher: z.string().min(1).max(240).describe("Source publisher or responsible organization."),
  retrievedAt: z.iso.date().describe("ISO date on which the source was retrieved."),
  note: z.string().max(2_000).optional().describe("Optional internal evidence note."),
  display: z
    .boolean()
    .default(false)
    .describe("Show this source in the reader-facing sources list."),
});

export const featurePageAssetSchema = z.object({
  id: z.uuid().describe("Asset UUID referenced by visual-source objects."),
  file: z.string().min(1).max(1_000).describe("Bundle-relative asset file path."),
  sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .describe("Lowercase SHA-256 checksum of the asset bytes."),
  mimeType: z
    .enum(["image/avif", "image/jpeg", "image/png", "image/svg+xml", "image/webp"])
    .describe("Supported image MIME type."),
  width: z.number().int().positive().describe("Intrinsic asset width in pixels."),
  height: z.number().int().positive().describe("Intrinsic asset height in pixels."),
  alt: z.string().min(1).max(500).describe("Accessible alternative text describing the image."),
  credit: z.string().min(1).max(500).describe("Required image attribution or creator credit."),
  license: z.string().min(1).max(240).describe("License name or rights statement."),
  sourceUrl: z.url().optional().describe("Optional original source URL for the asset."),
});

export const featurePageCollectionEmblemSchema = z.discriminatedUnion("source", [
  z.object({
    source: z.literal("iconify").describe("Resolve the collection emblem from Iconify."),
    prefix: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .refine((value) => !value.startsWith("material-symbols"), {
        message: "Material Symbols cannot be used as collection emblems",
      })
      .describe("Iconify collection prefix, excluding Material Symbols sets."),
    name: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .describe("Icon name within the selected Iconify collection."),
    author: z.string().min(1).max(240).describe("Icon author or collection creator."),
    sourceUrl: z
      .url()
      .refine((value) => new URL(value).hostname === "api.iconify.design", {
        message: "Iconify emblems must use api.iconify.design",
      })
      .describe("Direct api.iconify.design source URL used to retrieve the icon."),
    licenseSpdx: z
      .enum(["CC0-1.0", "MIT", "ISC", "Apache-2.0", "CC-BY-3.0", "CC-BY-4.0"])
      .describe("SPDX identifier for the icon license."),
    licenseUrl: z.url().describe("Canonical URL describing the icon license."),
    rationale: z
      .string()
      .min(20)
      .max(1_000)
      .describe("Editorial rationale for choosing this emblem."),
  }),
  z.object({
    source: z.literal("ownedSvg").describe("Use an SVG included in bundle assets."),
    assetId: z.uuid().describe("SVG asset UUID used as the collection emblem."),
    rationale: z
      .string()
      .min(20)
      .max(1_000)
      .describe("Editorial rationale for choosing this emblem."),
  }),
]);

const featurePageWishlistResolutionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("release").describe("Resolve the wishlist target to a physical release."),
    releaseCatalogRefId: z.uuid().describe("Release catalog reference UUID selected for wishlist."),
    actualFormat: z
      .enum(["vinyl", "cd", "cassette", "other"])
      .describe("Physical format of the selected release."),
    selection: z
      .enum(["preferredFormat", "crossFormatFallback"])
      .describe("Whether the release satisfies the preferred format or is a fallback."),
    rationale: z
      .string()
      .min(10)
      .max(1_000)
      .describe("Editorial reason for selecting this release."),
  }),
  z.object({
    kind: z.literal("unavailable").describe("Mark that no suitable wishlist release is available."),
    reason: z
      .string()
      .min(10)
      .max(1_000)
      .describe("Reason no suitable physical release could be selected."),
  }),
]);

const featurePageCollectionBaseSchema = z.object({
  kind: z
    .enum(["ranked", "curated"])
    .describe("Whether item order is a formal ranking or an editorial sequence."),
  itemEntityType: z
    .enum(["work", "release"])
    .describe("Catalog entity type accepted as collection members."),
  ownership: z
    .discriminatedUnion("mode", [
      z.object({
        mode: z
          .literal("anyReleaseOfWork")
          .describe("Owning any physical release of the work satisfies collection ownership."),
      }),
      z.object({
        mode: z
          .literal("exactRelease")
          .describe("Only the exact referenced physical release satisfies collection ownership."),
      }),
    ])
    .describe("Ownership rule used when evaluating a user's collection."),
  wishlist: z
    .discriminatedUnion("mode", [
      z.object({
        mode: z
          .literal("curatedByFormat")
          .describe("Use an editorially selected release for the preferred physical format."),
        supportedFormats: z
          .tuple([z.literal("vinyl")])
          .describe("Preferred physical formats supported by this collection policy."),
        fallbackOrder: z
          .tuple([z.literal("cd"), z.literal("cassette"), z.literal("other")])
          .describe("Physical format fallback priority when vinyl is unavailable."),
      }),
      z.object({
        mode: z.literal("exactRelease").describe("Wishlist the exact referenced physical release."),
      }),
    ])
    .describe("Wishlist behavior for collection members."),
  origin: z
    .discriminatedUnion("kind", [
      z.object({
        kind: z.literal("external").describe("Collection originates from an external publication."),
        publisher: z.string().min(1).max(240).describe("External publisher name."),
        title: z.string().min(1).max(500).describe("Title of the external list or feature."),
        url: z.url().describe("Canonical URL of the external collection source."),
        editionLabel: z
          .string()
          .min(1)
          .max(240)
          .optional()
          .describe("Optional edition, issue, or list-version label."),
        publishedAt: z.iso.date().optional().describe("Optional original publication date."),
        retrievedAt: z.iso.date().describe("Date the external collection was retrieved."),
      }),
      z.object({
        kind: z
          .literal("garimpaEditorial")
          .describe("Collection is an original Garimpa editorial selection."),
      }),
    ])
    .describe("Provenance of the collection membership and ordering."),
  emblem: featurePageCollectionEmblemSchema.describe(
    "Licensed emblem representing the collection.",
  ),
  items: z
    .array(
      z.object({
        catalogRefId: z.uuid().describe("Catalog reference UUID of the collection member."),
        annotation: featurePageCollectionAnnotationSchema
          .optional()
          .describe("Optional reader-facing badge and note for this collection member."),
        position: z
          .number()
          .int()
          .min(1)
          .max(500)
          .optional()
          .describe("One-based formal ranking position for ranked collections."),
        wishlistTarget: z
          .object({
            requestedFormat: z
              .literal("vinyl")
              .describe("Preferred format requested by the collection policy."),
            resolution: featurePageWishlistResolutionSchema.describe(
              "Resolved physical release or documented unavailability.",
            ),
          })
          .optional()
          .describe("Wishlist resolution required for work-based collection members."),
      }),
    )
    .min(1)
    .max(500)
    .describe("Ordered authoritative collection members."),
});

type FeaturePageCollectionInput = z.infer<typeof featurePageCollectionBaseSchema>;
type CollectionRefinementContext = Parameters<
  Parameters<typeof featurePageCollectionBaseSchema.superRefine>[0]
>[1];

function collectionPositions(collection: FeaturePageCollectionInput): number[] {
  return collection.items.flatMap((item) => (item.position === undefined ? [] : [item.position]));
}

function validateUniqueCollectionValues(
  collection: FeaturePageCollectionInput,
  positions: number[],
  context: CollectionRefinementContext,
): void {
  const memberIds = collection.items.map((item) => item.catalogRefId);
  if (new Set(memberIds).size !== memberIds.length) {
    context.addIssue({
      code: "custom",
      path: ["items"],
      message: "Collection members must be unique",
      input: collection,
    });
  }
  if (new Set(positions).size !== positions.length) {
    context.addIssue({
      code: "custom",
      path: ["items"],
      message: "Collection positions must be unique",
      input: collection,
    });
  }
}

function validateRankedCollection(
  collection: FeaturePageCollectionInput,
  positions: number[],
  context: CollectionRefinementContext,
): void {
  if (collection.kind !== "ranked") return;
  if (positions.length !== collection.items.length) {
    context.addIssue({
      code: "custom",
      path: ["items"],
      message: "Ranked collections require every position",
      input: collection,
    });
    return;
  }
  const ordered = [...positions].sort((left, right) => left - right);
  if (ordered.some((position, index) => position !== index + 1)) {
    context.addIssue({
      code: "custom",
      path: ["items"],
      message: "Ranked positions must be contiguous from 1",
      input: collection,
    });
  }
}

function validateWorkCollection(
  collection: FeaturePageCollectionInput,
  context: CollectionRefinementContext,
): void {
  if (collection.ownership.mode !== "anyReleaseOfWork") {
    context.addIssue({
      code: "custom",
      path: ["ownership"],
      message: "Work collections require anyReleaseOfWork ownership",
      input: collection,
    });
  }
  if (collection.wishlist.mode !== "curatedByFormat") {
    context.addIssue({
      code: "custom",
      path: ["wishlist"],
      message: "Work collections require curatedByFormat wishlist targets",
      input: collection,
    });
  }
  collection.items.forEach((item, index) => {
    if (!item.wishlistTarget) {
      context.addIssue({
        code: "custom",
        path: ["items", index, "wishlistTarget"],
        message: "Work collection members require one wishlist resolution",
        input: item,
      });
    }
  });
}

function validateReleaseCollection(
  collection: FeaturePageCollectionInput,
  context: CollectionRefinementContext,
): void {
  if (collection.ownership.mode !== "exactRelease") {
    context.addIssue({
      code: "custom",
      path: ["ownership"],
      message: "Release collections require exactRelease ownership",
      input: collection,
    });
  }
  if (collection.wishlist.mode !== "exactRelease") {
    context.addIssue({
      code: "custom",
      path: ["wishlist"],
      message: "Release collections require exactRelease wishlist behavior",
      input: collection,
    });
  }
  collection.items.forEach((item, index) => {
    if (item.wishlistTarget) {
      context.addIssue({
        code: "custom",
        path: ["items", index, "wishlistTarget"],
        message:
          "Release collection members target their exact release and must omit wishlistTarget",
        input: item,
      });
    }
  });
}

function validateCollectionEntityPolicy(
  collection: FeaturePageCollectionInput,
  context: CollectionRefinementContext,
): void {
  if (collection.itemEntityType === "work") {
    validateWorkCollection(collection, context);
    return;
  }
  validateReleaseCollection(collection, context);
}

export const featurePageCollectionSchema = featurePageCollectionBaseSchema.superRefine(
  (collection, context) => {
    const positions = collectionPositions(collection);
    validateUniqueCollectionValues(collection, positions, context);
    validateRankedCollection(collection, positions, context);
    validateCollectionEntityPolicy(collection, context);
  },
);

export const featurePageCollectionPresentationSchema = z.object({
  label: z.string().min(1).max(180).describe("Localized reader-facing collection label."),
  emblemLabel: z
    .string()
    .min(1)
    .max(240)
    .describe("Accessible localized label for the collection emblem."),
  cardVisual: featurePageVisualSourceSchema
    .optional()
    .describe("Legacy collection-only landing-card visual. New revisions use revision.cardVisual."),
  defaultSort: z
    .enum([
      "rankingAsc",
      "rankingDesc",
      "titleAsc",
      "titleDesc",
      "artistAsc",
      "artistDesc",
      "editorial",
    ])
    .default("rankingAsc")
    .describe("Default member ordering on the rendered collection page."),
  pageSize: z
    .number()
    .int()
    .min(12)
    .max(60)
    .default(36)
    .describe("Number of collection members displayed per page."),
});

export const featurePageArtistDirectorySchema = z.object({
  breadcrumbLabel: z
    .string()
    .trim()
    .min(1)
    .max(160)
    .describe("Localized label used when this artist directory appears in breadcrumbs."),
});

const featurePageRevisionSchema = z.object({
  seo: featurePageSeoSchema.describe("Localized search and social metadata."),
  theme: featurePageThemeSchema
    .default({ surfaceStyle: "dark" })
    .describe("Visual theme applied to the page revision."),
  cardVisual: featurePageVisualSourceSchema
    .optional()
    .describe("Explicit visual used on home and family landing cards."),
  artistDirectory: featurePageArtistDirectorySchema
    .optional()
    .describe("Localized artist-directory presentation metadata for genre and style pages."),
  document: featurePageDocumentSchema.describe("Localized editorial document content."),
  collection: featurePageCollectionSchema
    .optional()
    .describe("Authoritative membership metadata required for collection pages."),
  collectionPresentation: featurePageCollectionPresentationSchema
    .optional()
    .describe("Localized presentation metadata required for collection pages."),
});

const featurePageBundleBaseSchema = z.object({
  format: z
    .literal("garimpa-feature-page")
    .describe("Bundle format discriminator; always garimpa-feature-page."),
  version: z.literal(1).describe("FeaturePageBundle schema version; currently 1."),
  page: z
    .object({
      id: z.uuid().describe("Stable page UUID shared by every locale revision."),
      family: featurePageFamilySchema.describe("Editorial family determining the page route."),
      slug: z
        .string()
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
        .describe("Canonical kebab-case route slug."),
      subject: z
        .object({
          type: featurePageFamilySchema.describe("Subject taxonomy or editorial family."),
          key: z
            .string()
            .regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/)
            .describe("Stable snake_case subject key."),
        })
        .describe("Canonical page subject identity."),
    })
    .describe("Locale-independent feature page identity."),
  locale: localeSchema.describe("Locale of this editorial revision: en, pt, or es."),
  publication: z
    .object({
      intent: z
        .enum(["draft", "publishIfValid"])
        .default("draft")
        .describe("Requested publication intent stored in the bundle."),
    })
    .describe("Publication intent for this revision."),
  aliases: z
    .array(
      z.object({
        family: featurePageFamilySchema.describe("Family of the historical alias route."),
        slug: z
          .string()
          .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
          .describe("Historical kebab-case slug redirected to the canonical page."),
      }),
    )
    .max(50)
    .default([])
    .describe("Historical routes that should redirect to this page."),
  catalogRefs: z
    .array(featurePageCatalogRefSchema)
    .max(1_100)
    .default([])
    .describe("Declared stable catalog entities available to blocks and relationships."),
  entityRelations: z
    .array(featurePageEntityRelationSchema)
    .max(200)
    .default([])
    .describe("Page-level editorial roles assigned to declared catalog references."),
  sources: z
    .array(featurePageSourceSchema)
    .max(200)
    .default([])
    .describe("Research sources available for citations and reader-facing display."),
  assets: z
    .array(featurePageAssetSchema)
    .max(100)
    .default([])
    .describe("Licensed image assets packaged with the bundle."),
  revision: featurePageRevisionSchema.describe("Localized editorial revision payload."),
});

type FeaturePageBundleInput = z.infer<typeof featurePageBundleBaseSchema>;
type BundleRefinementContext = Parameters<
  Parameters<typeof featurePageBundleBaseSchema.superRefine>[0]
>[1];

function validateBundleIdentity(
  bundle: FeaturePageBundleInput,
  context: BundleRefinementContext,
): void {
  if (bundle.page.family !== bundle.page.subject.type) {
    context.addIssue({
      code: "custom",
      path: ["page", "subject", "type"],
      message: "Subject type must match page family",
    });
  }

  const blockIds = bundle.revision.document.blocks.map((block) => block.id);
  if (new Set(blockIds).size !== blockIds.length) {
    context.addIssue({
      code: "custom",
      path: ["revision", "document", "blocks"],
      message: "Block ids must be unique",
    });
  }

  const refIds = bundle.catalogRefs.map((ref) => ref.id);
  if (new Set(refIds).size !== refIds.length) {
    context.addIssue({
      code: "custom",
      path: ["catalogRefs"],
      message: "Catalog ref ids must be unique",
    });
  }

  const sourceIds = bundle.sources.map((source) => source.id);
  if (new Set(sourceIds).size !== sourceIds.length) {
    context.addIssue({ code: "custom", path: ["sources"], message: "Source ids must be unique" });
  }

  const relationRefIds = bundle.entityRelations.map((relation) => relation.catalogRefId);
  if (new Set(relationRefIds).size !== relationRefIds.length) {
    context.addIssue({
      code: "custom",
      path: ["entityRelations"],
      message: "Entity relation catalog refs must be unique",
    });
  }
}

function collectionForBundleValidation(
  bundle: FeaturePageBundleInput,
  context: BundleRefinementContext,
): FeaturePageCollectionInput | null {
  if (bundle.page.family !== "collection") {
    if (bundle.revision.collection || bundle.revision.collectionPresentation) {
      context.addIssue({
        code: "custom",
        path: ["revision", "collection"],
        message: "Only collection pages can define collection metadata",
      });
    }
    return null;
  }
  const isDraft = bundle.publication.intent === "draft";
  if (!bundle.revision.collection) {
    if (isDraft) return null;
    context.addIssue({
      code: "custom",
      path: ["revision", "collection"],
      message: "Collection pages require authoritative collection metadata",
    });
  }
  if (!bundle.revision.collectionPresentation) {
    if (isDraft) return null;
    context.addIssue({
      code: "custom",
      path: ["revision", "collectionPresentation"],
      message: "Collection pages require localized collection presentation",
    });
  }
  return bundle.revision.collection ?? null;
}

type BundleCatalogRef = FeaturePageBundleInput["catalogRefs"][number];
type BundleCollectionItem = FeaturePageCollectionInput["items"][number];

function validateCollectionMemberReference(
  item: BundleCollectionItem,
  index: number,
  collection: FeaturePageCollectionInput,
  refsById: ReadonlyMap<string, BundleCatalogRef>,
  context: BundleRefinementContext,
): void {
  const member = refsById.get(item.catalogRefId);
  if (!member) {
    context.addIssue({
      code: "custom",
      path: ["revision", "collection", "items", index, "catalogRefId"],
      message: "Collection members must reference declared catalog refs",
    });
    return;
  }
  if (member.entityType !== collection.itemEntityType) {
    context.addIssue({
      code: "custom",
      path: ["revision", "collection", "items", index, "catalogRefId"],
      message: `Collection member must reference ${collection.itemEntityType}`,
    });
  }
}

function validateWishlistResolution(
  item: BundleCollectionItem,
  index: number,
  refsById: ReadonlyMap<string, BundleCatalogRef>,
  context: BundleRefinementContext,
): void {
  const resolution = item.wishlistTarget?.resolution;
  if (resolution?.kind !== "release") return;
  const target = refsById.get(resolution.releaseCatalogRefId);
  const path = [
    "revision",
    "collection",
    "items",
    index,
    "wishlistTarget",
    "resolution",
    "releaseCatalogRefId",
  ];
  if (!target) {
    context.addIssue({
      code: "custom",
      path,
      message: "Wishlist targets must reference declared catalog refs",
    });
  } else if (target.entityType !== "release") {
    context.addIssue({ code: "custom", path, message: "Wishlist targets must reference releases" });
  }
  if (resolution.selection === "preferredFormat" && resolution.actualFormat !== "vinyl") {
    context.addIssue({
      code: "custom",
      path: [...path.slice(0, -1), "actualFormat"],
      message: "Preferred-format targets must be vinyl",
    });
  }
  if (resolution.selection === "crossFormatFallback" && resolution.actualFormat === "vinyl") {
    context.addIssue({
      code: "custom",
      path: [...path.slice(0, -1), "actualFormat"],
      message: "Cross-format fallbacks must be CD, cassette, or other",
    });
  }
}

function validateBundleCollectionItems(
  bundle: FeaturePageBundleInput,
  collection: FeaturePageCollectionInput,
  context: BundleRefinementContext,
): void {
  const refsById = new Map(bundle.catalogRefs.map((ref) => [ref.id, ref]));
  collection.items.forEach((item, index) => {
    validateCollectionMemberReference(item, index, collection, refsById, context);
    validateWishlistResolution(item, index, refsById, context);
  });
}

function validateCollectionEmblem(
  bundle: FeaturePageBundleInput,
  collection: FeaturePageCollectionInput,
  context: BundleRefinementContext,
): void {
  const emblem = collection.emblem;
  if (emblem.source !== "ownedSvg") return;
  const asset = bundle.assets.find((candidate) => candidate.id === emblem.assetId);
  if (asset && asset.mimeType !== "image/svg+xml") {
    context.addIssue({
      code: "custom",
      path: ["revision", "collection", "emblem", "assetId"],
      message: "Owned collection emblems must reference SVG assets",
    });
  }
}

export const featurePageBundleSchema = featurePageBundleBaseSchema.superRefine(
  (bundle, context) => {
    validateBundleIdentity(bundle, context);
    const collection = collectionForBundleValidation(bundle, context);
    if (!collection) return;
    validateBundleCollectionItems(bundle, collection, context);
    validateCollectionEmblem(bundle, collection, context);
  },
);

export type FeaturePageBundle = z.infer<typeof featurePageBundleSchema>;

export type FeaturePageFamily = z.infer<typeof featurePageFamilySchema>;
export type FeaturePageEntityType = z.infer<typeof featurePageEntityTypeSchema>;
export type FeaturePageVisualSource = z.infer<typeof featurePageVisualSourceSchema>;
export type FeaturePageBackground = z.infer<typeof featurePageBackgroundSchema>;
export type FeaturePageEntitySelector = z.infer<typeof featurePageEntitySelectorSchema>;
export type FeaturePageBlock = z.infer<typeof featurePageBlockSchema>;
export type FeaturePageDocument = z.infer<typeof featurePageDocumentSchema>;
export type FeaturePageSeo = z.infer<typeof featurePageSeoSchema>;
export type FeaturePageTheme = z.infer<typeof featurePageThemeSchema>;
export type FeaturePageArtistDirectory = z.infer<typeof featurePageArtistDirectorySchema>;
export type FeaturePageCatalogRef = z.infer<typeof featurePageCatalogRefSchema>;
export type FeaturePageEntityRelation = z.infer<typeof featurePageEntityRelationSchema>;
export type FeaturePageEntityRelationRole = z.infer<typeof featurePageEntityRelationRoleSchema>;
export type FeaturePageSource = z.infer<typeof featurePageSourceSchema>;
export type FeaturePageAsset = z.infer<typeof featurePageAssetSchema>;
export type FeaturePageCollectionAnnotation = z.infer<typeof featurePageCollectionAnnotationSchema>;
export type FeaturePageCollection = z.infer<typeof featurePageCollectionSchema>;
export type FeaturePageCollectionPresentation = z.infer<
  typeof featurePageCollectionPresentationSchema
>;

export function featurePageBundleJsonSchema() {
  return z.toJSONSchema(featurePageBundleSchema);
}

export interface FeaturePageDocumentReference {
  id: string;
  path: string;
  required: boolean;
}

export interface FeaturePageBundleReferenceValidation {
  assetReferences: FeaturePageDocumentReference[];
  catalogReferences: FeaturePageDocumentReference[];
  errors: string[];
  sourceReferences: FeaturePageDocumentReference[];
}

function nestedReferencePath(path: string, key: string): string {
  return path ? `${path}.${key}` : key;
}

function isSingularReferenceKey(key: string, singularKey: string): boolean {
  const suffix = `${singularKey.charAt(0).toUpperCase()}${singularKey.slice(1)}`;
  return key === singularKey || key.endsWith(suffix);
}

function collectStringArrayReferences(
  value: unknown[],
  path: string,
  target: FeaturePageDocumentReference[],
): void {
  value.forEach((id, index) => {
    if (typeof id === "string") {
      target.push({ id, path: `${path}[${index}]`, required: true });
    }
  });
}

function collectNamedReferences(
  value: unknown,
  path: string,
  target: FeaturePageDocumentReference[],
  singularKey: string,
  pluralKey: string,
): void {
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      collectNamedReferences(item, `${path}[${index}]`, target, singularKey, pluralKey),
    );
    return;
  }
  if (!isPlainObject(value)) return;

  for (const [key, child] of Object.entries(value)) {
    const childPath = nestedReferencePath(path, key);
    if (isSingularReferenceKey(key, singularKey) && typeof child === "string") {
      target.push({ id: child, path: childPath, required: true });
      continue;
    }
    if (key === pluralKey && Array.isArray(child)) {
      collectStringArrayReferences(child, childPath, target);
      continue;
    }
    collectNamedReferences(child, childPath, target, singularKey, pluralKey);
  }
}

function collectCatalogReferences(bundle: FeaturePageBundle): FeaturePageDocumentReference[] {
  const references: FeaturePageDocumentReference[] = [];
  collectNamedReferences(
    bundle.revision.cardVisual,
    "revision.cardVisual",
    references,
    "catalogRefId",
    "catalogRefIds",
  );
  collectNamedReferences(
    bundle.revision.document,
    "revision.document",
    references,
    "catalogRefId",
    "pinnedCatalogRefIds",
  );
  bundle.entityRelations.forEach((relation, index) => {
    references.push({
      id: relation.catalogRefId,
      path: `entityRelations[${index}].catalogRefId`,
      required: true,
    });
  });
  bundle.revision.collection?.items.forEach((item, index) => {
    references.push({
      id: item.catalogRefId,
      path: `revision.collection.items[${index}].catalogRefId`,
      required: true,
    });
    const resolution = item.wishlistTarget?.resolution;
    if (resolution?.kind === "release") {
      references.push({
        id: resolution.releaseCatalogRefId,
        path: `revision.collection.items[${index}].wishlistTarget.resolution.releaseCatalogRefId`,
        required: true,
      });
    }
  });
  return references;
}

function undeclaredReferenceErrors(
  references: FeaturePageDocumentReference[],
  declaredIds: ReadonlySet<string>,
  label: string,
): string[] {
  return references
    .filter((reference) => !declaredIds.has(reference.id))
    .map((reference) => `${reference.path} uses undeclared ${label} ${reference.id}`);
}

function entityCollectionTypeErrors(bundle: FeaturePageBundle): string[] {
  const errors: string[] = [];
  for (const block of bundle.revision.document.blocks) {
    if (block.type !== "entityCollection" || !block.pinnedCatalogRefIds) continue;
    for (const refId of block.pinnedCatalogRefIds) {
      const declaration = bundle.catalogRefs.find((ref) => ref.id === refId);
      if (declaration && declaration.entityType !== block.entityType) {
        errors.push(
          `revision.document.blocks.${block.id} expects ${block.entityType} but ref ${refId} is ${declaration.entityType}`,
        );
      }
    }
  }
  return errors;
}

function collectionItemTypeErrors(
  item: FeaturePageCollection["items"][number],
  index: number,
  collection: FeaturePageCollection,
  catalogRefs: FeaturePageCatalogRef[],
): string[] {
  const errors: string[] = [];
  const member = catalogRefs.find((ref) => ref.id === item.catalogRefId);
  if (member && member.entityType !== collection.itemEntityType) {
    errors.push(
      `revision.collection.items[${index}].catalogRefId expects ${collection.itemEntityType} but ref ${item.catalogRefId} is ${member.entityType}`,
    );
  }
  const resolution = item.wishlistTarget?.resolution;
  if (resolution?.kind !== "release") return errors;
  const release = catalogRefs.find((ref) => ref.id === resolution.releaseCatalogRefId);
  if (release && release.entityType !== "release") {
    errors.push(
      `revision.collection.items[${index}].wishlistTarget.resolution.releaseCatalogRefId expects release but ref ${resolution.releaseCatalogRefId} is ${release.entityType}`,
    );
  }
  return errors;
}

function collectionTypeErrors(bundle: FeaturePageBundle): string[] {
  const collection = bundle.revision.collection;
  if (!collection) return [];
  return collection.items.flatMap((item, index) =>
    collectionItemTypeErrors(item, index, collection, bundle.catalogRefs),
  );
}

export function inspectFeaturePageBundleReferences(
  rawBundle: FeaturePageBundle,
): FeaturePageBundleReferenceValidation {
  const bundle = featurePageBundleSchema.parse(rawBundle);
  const catalogReferences = collectCatalogReferences(bundle);
  const sourceReferences: FeaturePageDocumentReference[] = [];
  const assetReferences: FeaturePageDocumentReference[] = [];
  collectNamedReferences(bundle.revision, "revision", sourceReferences, "sourceId", "sourceIds");
  collectNamedReferences(bundle.revision, "revision", assetReferences, "assetId", "assetIds");

  const errors = [
    ...undeclaredReferenceErrors(
      catalogReferences,
      new Set(bundle.catalogRefs.map((ref) => ref.id)),
      "catalog ref",
    ),
    ...undeclaredReferenceErrors(
      sourceReferences,
      new Set(bundle.sources.map((source) => source.id)),
      "source",
    ),
    ...undeclaredReferenceErrors(
      assetReferences,
      new Set(bundle.assets.map((asset) => asset.id)),
      "asset",
    ),
    ...entityCollectionTypeErrors(bundle),
    ...collectionTypeErrors(bundle),
  ];

  return { assetReferences, catalogReferences, errors, sourceReferences };
}
